// ============================================================================
// CrewClockCard — the Crew Clock's spot on the dashboard home.
// ----------------------------------------------------------------------------
// Beta-tester ask (via John, 2026-09-26): the clock should be "right on the
// dashboard so it's there as soon as you open your phone." This card shows
// who's on the clock RIGHT NOW (live elapsed time, break state) and is one
// tap from the full Crew Clock page. It reads the same live-entries query
// the clock page uses; clock actions themselves stay on the full page so
// there's exactly one place that mutates shifts.
// ============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, ChevronRight, Coffee } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  fetchAllActiveEntries,
  computeElapsedSeconds,
} from '@/lib/timeEntriesData';
import { useCrewRoster } from '@/lib/crewRoster';

const fmtElapsed = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

export default function CrewClockCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState(null); // null = loading
  const { allRoster } = useCrewRoster();
  const subIds = new Set(allRoster.filter((m) => m.type === 'sub').map((m) => m.id));
  // Re-render every 60s so elapsed times stay honest while the tab is open.
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const rows = await fetchAllActiveEntries();
      setEntries(rows || []);
    } catch {
      // Non-fatal: the dashboard must never break over the clock widget.
      setEntries([]);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(timer);
  }, [load]);

  const open = () => navigate('/dashboard/crew-clock');
  const active = entries || [];

  return (
    <Card
      className="border-border bg-[hsl(var(--card))] cursor-pointer hover:border-[hsl(var(--primary))]/40 transition-colors"
      onClick={open}
      data-testid="dashboard-crew-clock"
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <Clock size={15} className="text-primary shrink-0" />
          <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            {t('dashboard.crew_clock_title', { defaultValue: 'Crew Clock' })}
          </span>
          {entries !== null ? (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium',
                active.length > 0
                  ? 'border-[hsl(150_56%_50%)]/35 bg-[hsl(150_56%_50%)]/10 text-[hsl(150_56%_60%)]'
                  : 'border-border bg-[hsl(var(--muted))] text-muted-foreground'
              )}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  active.length > 0 ? 'bg-[hsl(150_56%_50%)]' : 'bg-muted-foreground'
                )}
              />
              {active.length > 0
                ? t('dashboard.crew_clock_on', {
                    defaultValue: '{{count}} on the clock',
                    count: active.length,
                  })
                : t('dashboard.crew_clock_none', { defaultValue: 'No one clocked in' })}
            </span>
          ) : null}
          <ChevronRight size={15} className="ml-auto shrink-0 text-muted-foreground" />
        </div>

        {active.length > 0 ? (
          <div className="mt-3 space-y-1.5">
            {active.map((e) => {
              const onBreak = e.status === 'on_break';
              return (
                <div key={e.id} className="flex items-center gap-2 text-sm">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full shrink-0',
                      onBreak ? 'bg-[hsl(38_92%_60%)]' : 'bg-[hsl(150_56%_50%)]'
                    )}
                  />
                  <span className="font-medium text-foreground truncate">{e.name}</span>
                  {subIds.has(e.crew_id) ? (
                    <span className="shrink-0 rounded border border-[hsl(var(--primary))]/40 px-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--primary))]">
                      {t('crewClock.sub_badge', { defaultValue: 'Sub' })}
                    </span>
                  ) : null}
                  {e.job ? (
                    <span className="text-xs text-muted-foreground truncate">{e.job}</span>
                  ) : null}
                  <span className="ml-auto shrink-0 tabular-nums text-xs text-muted-foreground inline-flex items-center gap-1">
                    {onBreak ? <Coffee size={11} /> : null}
                    {fmtElapsed(computeElapsedSeconds(e))}
                  </span>
                </div>
              );
            })}
          </div>
        ) : entries !== null ? (
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {t('dashboard.crew_clock_empty_hint', {
                defaultValue: 'Tap to clock the crew in.',
              })}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.14em] text-[11px]"
              onClick={(ev) => {
                ev.stopPropagation();
                open();
              }}
              data-testid="dashboard-crew-clock-open"
            >
              {t('dashboard.crew_clock_open', { defaultValue: 'Clock in' })}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
